import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { assignmentAPI, categoryAPI, aiAPI, aiInstructionTemplateAPI } from '../services/api';
import MemoizedSyntaxHighlighter from './MemoizedSyntaxHighlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FormattedText } from '../utils/formatText';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

const CodingAssignmentForm = ({ onSuccess, assignment, onAddToCart, cartMode = false, prefillData, onPrefillDataUsed }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    problemStatement: '',
    complexity: 'Medium',
    developerLevel: 'juniorDeveloper',
    languages: [],
    starterCodeMode: 'withFunction',
    category: '',
    tags: [],
    estimatedTime: 60,
    additionalInstructions: '',
    sourceCode: '',
    sourceCodeLanguage: 'javascript'
  });

  const [categories, setCategories] = useState([]);
  const [instructionTemplates, setInstructionTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [algorithmByLanguage, setAlgorithmByLanguage] = useState(null);
  const [pseudocodeByLanguage, setPseudocodeByLanguage] = useState(null);
  const [editingPseudocode, setEditingPseudocode] = useState(null); // { language: '', text: '' }
  const [savingPseudocode, setSavingPseudocode] = useState(false);
  const [previewLanguage, setPreviewLanguage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assignmentCode, setAssignmentCode] = useState('');
  const [duplicateAssignment, setDuplicateAssignment] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [checkingTitle, setCheckingTitle] = useState(false);
  const isEditMode = !!assignment;

  const developerLevels = [
    { value: 'internDeveloper', label: 'Intern Developer', steps: '6-8 steps, 4 hints each', desc: 'For absolute beginners with no prior experience', color: '#48bb78' },
    { value: 'juniorDeveloper', label: 'Junior Developer', steps: '4-5 steps, 3 hints each', desc: 'For those with basic programming knowledge', color: '#667eea' },
    { value: 'midLevelDeveloper', label: 'Mid-Level Developer', steps: '3-4 steps, 2-3 hints each', desc: 'For developers with solid fundamentals', color: '#fbbf24' },
    { value: 'seniorDeveloper', label: 'Senior Developer', steps: '2-3 steps, 1-2 hints each', desc: 'For experienced developers', color: '#f97316' },
    { value: 'leadDeveloper', label: 'Lead Developer', steps: '1-2 steps, 1 hint each', desc: 'For experts focused on architecture', color: '#ef4444' }
  ];

  const availableLanguages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'sql', label: 'SQL' },
    { value: 'csharp', label: 'C#' },
    { value: 'typescript', label: 'TypeScript' }
  ];

  useEffect(() => {
    fetchCategories();
    fetchInstructionTemplates();
  }, []);

  useEffect(() => {
    if (assignment) {
      setFormData({
        title: assignment.title || '',
        description: assignment.description || '',
        problemStatement: assignment.problemStatement || '',
        complexity: assignment.complexity || 'Medium',
        developerLevel: assignment.developerLevel || 'juniorDeveloper',
        languages: assignment.languages || [],
        starterCodeMode: assignment.starterCodeMode || 'withFunction',
        category: assignment.category?._id || assignment.category || '',
        tags: (assignment.tags || []).map(t => ({ value: t.name || t, label: t.name || t })),
        estimatedTime: assignment.estimatedTime || 60,
        additionalInstructions: assignment.additionalInstructions || '',
        sourceCode: assignment.sourceCode || '',
        sourceCodeLanguage: assignment.sourceCodeLanguage || 'javascript'
      });
      setAssignmentCode(assignment.assignmentCode || '');
      setAlgorithmByLanguage(assignment.algorithmByLanguage || null);
      setPseudocodeByLanguage(assignment.pseudocodeByLanguage || null);
      if (assignment.languages && assignment.languages.length > 0) {
        setPreviewLanguage(assignment.languages[0]);
      }
    }
  }, [assignment]);

  // Handle prefillData from cart items
  useEffect(() => {
    if (prefillData) {
      setFormData({
        title: prefillData.title || '',
        description: prefillData.description || '',
        problemStatement: prefillData.problemStatement || '',
        complexity: prefillData.complexity || 'Medium',
        developerLevel: prefillData.developerLevel || 'juniorDeveloper',
        languages: prefillData.languages || [],
        starterCodeMode: prefillData.starterCodeMode || 'withFunction',
        category: prefillData.category || '',
        tags: (prefillData.tags || []).map(t => 
          typeof t === 'string' ? { value: t, label: t } : t
        ),
        estimatedTime: prefillData.estimatedTime || 60,
        additionalInstructions: prefillData.additionalInstructions || '',
        sourceCode: prefillData.sourceCode || '',
        sourceCodeLanguage: prefillData.sourceCodeLanguage || 'javascript'
      });
      setAlgorithmByLanguage(prefillData.algorithmByLanguage || null);
      setPseudocodeByLanguage(prefillData.pseudocodeByLanguage || null);
      if (prefillData.languages && prefillData.languages.length > 0) {
        setPreviewLanguage(prefillData.languages[0]);
      }
      setAssignmentCode('');
      
      // Notify parent that prefill data has been used
      if (onPrefillDataUsed) {
        onPrefillDataUsed();
      }
    }
  }, [prefillData, onPrefillDataUsed]);

  // Debounced title duplicate check
  useEffect(() => {
    if (!formData.title || !formData.title.trim() || isEditMode) {
      setDuplicateAssignment(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkTitleDuplicate(formData.title);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [formData.title, isEditMode]);

  const fetchCategories = async () => {
    try {
      const categoriesRes = await categoryAPI.getAllCategories();
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchInstructionTemplates = async () => {
    try {
      const response = await aiInstructionTemplateAPI.getActiveTemplates();
      setInstructionTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching instruction templates:', err);
    }
  };

  const checkTitleDuplicate = async (title) => {
    if (!title || !title.trim() || isEditMode) {
      setDuplicateAssignment(null);
      return;
    }

    try {
      setCheckingTitle(true);
      const response = await assignmentAPI.checkTitle(title.trim());
      
      if (response.data.exists) {
        setDuplicateAssignment(response.data.assignment);
        setShowDuplicateModal(true);
      } else {
        setDuplicateAssignment(null);
      }
    } catch (err) {
      console.error('Error checking title:', err);
    } finally {
      setCheckingTitle(false);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }

    const template = instructionTemplates.find(t => t._id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      handleInputChange('additionalInstructions', template.content);
      
      // Track template usage
      try {
        await aiInstructionTemplateAPI.useTemplate(templateId);
      } catch (err) {
        console.error('Error tracking template usage:', err);
      }
    }
  };

  const handleLanguageToggle = useCallback((langValue) => {
    const newLanguages = formData.languages.includes(langValue)
      ? formData.languages.filter(l => l !== langValue)
      : [...formData.languages, langValue];
    handleInputChange('languages', newLanguages);
  }, [formData.languages, handleInputChange]);

  const handleTagChange = (newValue) => {
    console.log('Tag change - newValue:', newValue);
    handleInputChange('tags', newValue || []);
  };

  const handleGenerateDescription = async () => {
    if (!formData.title || !formData.title.trim()) {
      setError('Please enter an assignment title first');
      return;
    }

    setGeneratingDescription(true);
    setError(null);

    try {
      const response = await aiAPI.generateAssignmentDescription(formData.title);
      handleInputChange('description', response.data.description);
    } catch (err) {
      console.error('Error generating description:', err);
      setError(err.response?.data?.message || 'Failed to generate description. Please try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleAddToCart = () => {
    // Validate form data
    if (!formData.title) {
      setError('Please enter an assignment title');
      return;
    }
    if (!formData.problemStatement) {
      setError('Please enter a problem statement');
      return;
    }
    if (formData.languages.length === 0) {
      setError('Please select at least one programming language');
      return;
    }

    // Clean up formData
    const cleanedData = {
      ...formData,
      category: formData.category || undefined,
      tags: formData.tags && formData.tags.length > 0 ? formData.tags.map(t => t.value) : undefined,
      id: Date.now() // Temporary ID for cart items
    };

    // Add to cart
    if (onAddToCart) {
      onAddToCart(cleanedData);
      setSuccess('Assignment added to cart! Add more or click "Generate All" to process.');
      
      // Reset form for next entry
      setFormData({
        title: '',
        description: '',
        problemStatement: '',
        complexity: 'Medium',
        developerLevel: 'juniorDeveloper',
        languages: [],
        starterCodeMode: 'withFunction',
        category: '',
        tags: [],
        estimatedTime: 60,
        additionalInstructions: '',
        sourceCode: '',
        sourceCodeLanguage: 'javascript'
      });
      
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleGenerateSteps = async () => {
    if (!formData.title) {
      setError('Please enter an assignment title');
      return;
    }
    if (!isEditMode && duplicateAssignment) {
      setError('This assignment title is already in use. Please choose a different title.');
      setShowDuplicateModal(true);
      return;
    }
    if (!formData.problemStatement) {
      setError('Please enter a problem statement');
      return;
    }
    if (formData.languages.length === 0) {
      setError('Please select at least one programming language');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      // Clean up formData - remove empty category and convert tags to array of names
      console.log('FormData.tags before mapping:', formData.tags);
      const mappedTags = formData.tags && formData.tags.length > 0 ? formData.tags.map(t => t.value) : undefined;
      console.log('Mapped tags:', mappedTags);
      
      const cleanedData = {
        ...formData,
        category: formData.category || undefined, // Don't send empty string
        tags: mappedTags
      };
      
      console.log(`${isEditMode ? 'Updating' : 'Creating'} assignment with data:`, cleanedData);
      
      let response;
      if (isEditMode) {
        // Update existing assignment
        response = await assignmentAPI.updateAssignment(assignment._id, cleanedData);
        console.log('Update assignment response:', response.data);
        
        // Update algorithmByLanguage and pseudocodeByLanguage if regenerated
        if (response.data.assignment.algorithmByLanguage) {
          setAlgorithmByLanguage(response.data.assignment.algorithmByLanguage);
          if (cleanedData.languages && cleanedData.languages.length > 0) {
            setPreviewLanguage(cleanedData.languages[0]);
          }
        }
        if (response.data.assignment.pseudocodeByLanguage) {
          setPseudocodeByLanguage(response.data.assignment.pseudocodeByLanguage);
        }
        
        setSuccess(response.data.message || 'Assignment updated successfully!');
      } else {
        // Create new assignment
        response = await assignmentAPI.createAssignment(cleanedData);
        console.log('Create assignment response:', response.data);
        
        if (response.data.assignment.algorithmByLanguage) {
          setAlgorithmByLanguage(response.data.assignment.algorithmByLanguage);
          if (formData.languages && formData.languages.length > 0) {
            setPreviewLanguage(formData.languages[0]);
          }
        } else {
          console.warn('No algorithmByLanguage in response');
        }
        if (response.data.assignment.pseudocodeByLanguage) {
          setPseudocodeByLanguage(response.data.assignment.pseudocodeByLanguage);
        }
        
        setAssignmentCode(response.data.assignment.assignmentCode);
        setSuccess(`Assignment created successfully! Code: ${response.data.assignment.assignmentCode}`);
      }
      
      console.log(`Assignment ${isEditMode ? 'updated' : 'created'} successfully:`, response.data);
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
      
      // Reset form only for creation
      if (!isEditMode) {
        setFormData({
          title: '',
          description: '',
          problemStatement: '',
          complexity: 'Medium',
          developerLevel: 'juniorDeveloper',
          languages: [],
          starterCodeMode: 'withFunction',
          category: '',
          tags: [],
          estimatedTime: 60,
          additionalInstructions: '',
          sourceCode: '',
          sourceCodeLanguage: 'javascript'
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || `Error ${isEditMode ? 'updating' : 'creating'} assignment`;
      const errorDetails = err.response?.data?.details || '';
      setError(`${errorMessage}${errorDetails ? '\n' + errorDetails : ''}`);
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} assignment:`, err);
      console.error('Error response:', err.response);
      console.error('Error details:', err.response?.data);
    } finally {
      setGenerating(false);
    }
  };

  const selectedLevelConfig = developerLevels.find(dl => dl.value === formData.developerLevel);

  // Memoize rendered steps to prevent unnecessary re-renders during scroll
  const renderedSteps = useMemo(() => {
    if (!previewLanguage || !algorithmByLanguage[previewLanguage]?.steps) {
      return null;
    }

    return algorithmByLanguage[previewLanguage].steps.map((step, index) => (
      <div key={`${previewLanguage}-step-${index}`} style={{
        marginBottom: index < algorithmByLanguage[previewLanguage].steps.length - 1 ? '20px' : '0',
        padding: '20px',
        border: '1.5px solid #e2e8f0',
        borderRadius: '12px',
        backgroundColor: '#f7fafc'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#667eea',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
            fontSize: '14px',
            marginRight: '12px',
            flexShrink: 0
          }}>
            {step.stepNumber}
          </div>
          <h4 style={{ color: '#2d3748', margin: 0, fontSize: '16px', fontWeight: '600' }}>
            {step.title}
          </h4>
        </div>
        
        <p style={{ color: '#4a5568', marginBottom: '16px', lineHeight: '1.6', fontSize: '14px' }}>
          {step.description}
        </p>

        {/* Hints Preview */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '10px',
            color: '#2d3748',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <span style={{ marginRight: '6px' }}>💡</span>
            Hints ({step.hints.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {step.hints.map((hint, hintIdx) => (
              <div key={hintIdx} style={{
                padding: '12px',
                backgroundColor: '#ffffff',
                borderLeft: '3px solid #fbbf24',
                borderRadius: '6px',
                border: '1px solid #fef3c7'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#4a5568', fontWeight: '500' }}>
                    Hint {hintIdx + 1}
                  </span>
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#f59e0b',
                    backgroundColor: '#fef3c7',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    -{hint.pointPenalty} pts
                  </span>
                </div>
                {hint.allowFollowUp && (
                  <div style={{ fontSize: '12px', color: '#718096', marginTop: '6px' }}>
                    ✓ Follow-up hints enabled (max {hint.maxFollowUps})
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Starter Code Preview */}
        {step.starterCode && step.starterCode.trim() !== '' && (
          <div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px',
              color: '#2d3748',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <span style={{ marginRight: '6px' }}>💻</span>
              Starter Code ({previewLanguage})
            </div>
            <MemoizedSyntaxHighlighter
              language={previewLanguage === 'csharp' ? 'csharp' : previewLanguage}
              style={vscDarkPlus}
              wrapLongLines={true}
              customStyle={{
                borderRadius: '8px',
                fontSize: '13px',
                maxHeight: '250px',
                overflow: 'auto',
                margin: 0
              }}
            >
              {step.starterCode || '// No starter code'}
            </MemoizedSyntaxHighlighter>
          </div>
        )}
      </div>
    ));
  }, [previewLanguage, algorithmByLanguage]);

  return (
    <>
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          color: '#ef4444',
          padding: '14px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #fecaca',
          whiteSpace: 'pre-wrap',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#d1fae5',
          color: '#065f46',
          padding: '14px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #9ae6b4',
          fontSize: '13px'
        }}>
          {success}
          <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋 Share code:</span>
            <span style={{ 
              backgroundColor: '#667eea', 
              color: 'white', 
              padding: '6px 14px', 
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '15px',
              letterSpacing: '1px'
            }}>{assignmentCode}</span>
          </div>
        </div>
      )}

      {/* Duplicate Assignment Modal */}
      {showDuplicateModal && duplicateAssignment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'start', marginBottom: '20px' }}>
              <div style={{ 
                fontSize: '36px', 
                marginRight: '12px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, color: '#1a202c', fontSize: '20px', fontWeight: '700' }}>
                  Duplicate Assignment Found
                </h3>
                <p style={{ margin: '8px 0 0', color: '#718096', fontSize: '14px' }}>
                  An assignment with this title already exists in the database
                </p>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f7fafc',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Title
                </label>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                  {duplicateAssignment.title}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Assignment Code
                </label>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#667eea',
                  fontFamily: 'monospace',
                  backgroundColor: '#edf2f7',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'inline-block'
                }}>
                  {duplicateAssignment.assignmentCode}
                </div>
              </div>

              {duplicateAssignment.description && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    Description
                  </label>
                  <div style={{ fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>
                    {duplicateAssignment.description.substring(0, 200)}
                    {duplicateAssignment.description.length > 200 && '...'}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                {duplicateAssignment.complexity && (
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      Complexity
                    </label>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: duplicateAssignment.complexity === 'Easy' ? '#48bb78' : 
                             duplicateAssignment.complexity === 'Medium' ? '#fbbf24' : '#ef4444',
                      backgroundColor: duplicateAssignment.complexity === 'Easy' ? '#f0fff4' : 
                                     duplicateAssignment.complexity === 'Medium' ? '#fffbeb' : '#fef2f2',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      {duplicateAssignment.complexity}
                    </div>
                  </div>
                )}

                {duplicateAssignment.developerLevel && (
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      Level
                    </label>
                    <div style={{ fontSize: '13px', color: '#4a5568' }}>
                      {duplicateAssignment.developerLevel.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                )}
              </div>

              {duplicateAssignment.languages && duplicateAssignment.languages.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    Languages
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {duplicateAssignment.languages.map((lang, idx) => (
                      <span key={idx} style={{
                        fontSize: '12px',
                        backgroundColor: '#edf2f7',
                        color: '#4a5568',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}>
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {duplicateAssignment.createdAt && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    Created On
                  </label>
                  <div style={{ fontSize: '13px', color: '#4a5568' }}>
                    {new Date(duplicateAssignment.createdAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            <div style={{
              backgroundColor: '#fff5f5',
              borderLeft: '4px solid #f56565',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#742a2a', lineHeight: '1.5' }}>
                <strong>Note:</strong> Please choose a different title for your assignment, or edit the existing assignment if you want to make changes.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateAssignment(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#4a5568',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#f7fafc'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
              >
                Close & Edit Title
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditMode && (
        <div style={{
          backgroundColor: '#eff6ff',
          color: '#1e40af',
          padding: '14px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #bfdbfe',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'start',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <div>
            <strong>Note:</strong> Changing the problem statement, complexity, developer level, languages, starter code mode, or additional instructions will trigger AI to regenerate the algorithm steps and hints.
          </div>
        </div>
      )}

      {/* Basic Information Section */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '24px', 
        borderRadius: '10px', 
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <h3 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>📝</span>
          Basic Information
        </h3>
        {/* Title */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Assignment Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="e.g., Reverse a String"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${duplicateAssignment ? '#f56565' : '#e2e8f0'}`,
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              transition: 'border 0.15s'
            }}
          />
          {checkingTitle && !isEditMode && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#718096', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
              Checking for duplicates...
            </div>
          )}
          {duplicateAssignment && !isEditMode && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#e53e3e', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>⚠️</span>
              This title is already in use. Please choose a different title.
            </div>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: '0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Description
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={generatingDescription || !formData.title}
              style={{
                marginLeft: '10px',
                padding: '4px 10px',
                fontSize: '11px',
                background: generatingDescription ? '#cbd5e0' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: generatingDescription || !formData.title ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s',
                opacity: generatingDescription || !formData.title ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!generatingDescription && formData.title) {
                  e.target.style.background = '#5568d3';
                }
              }}
              onMouseLeave={(e) => {
                if (!generatingDescription) {
                  e.target.style.background = '#667eea';
                }
              }}
            >
              {generatingDescription ? '✨ Generating...' : '✨ Generate with AI'}
            </button>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief overview of the assignment"
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              fontFamily: 'inherit',
              resize: 'vertical',
              transition: 'border 0.15s'
            }}
          />
        </div>
      </div>

      {/* Problem Section */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '24px', 
        borderRadius: '10px', 
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <h3 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🎯</span>
          Problem Definition
        </h3>

        {/* Problem Statement */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Problem Statement * <span style={{ fontWeight: 'normal', color: '#718096' }}>(Supports rich text formatting)</span>
          </label>
          <textarea
            value={formData.problemStatement}
            onChange={(e) => handleInputChange('problemStatement', e.target.value)}
            placeholder="Write a function to reverse a string.&#10;&#10;Example:&#10;Input: 'hello'&#10;Output: 'olleh'&#10;&#10;You can use inline code like `reverseString()` or code blocks:&#10;```javascript&#10;function reverseString(str) {&#10;  // your code here&#10;}&#10;```"
            rows={8}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              resize: 'vertical',
              transition: 'border 0.15s',
              lineHeight: '1.6'
            }}
          />
          <div style={{ 
            marginTop: '6px', 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'start',
            fontSize: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#4a5568', fontWeight: '600', marginBottom: '4px' }}>💡 Formatting Tips:</div>
              <ul style={{ color: '#718096', margin: '0', paddingLeft: '18px', lineHeight: '1.5' }}>
                <li>Inline code: <code style={{ backgroundColor: '#f7fafc', padding: '2px 4px', borderRadius: '3px', fontSize: '11px' }}>`code`</code></li>
                <li>Code blocks: <code style={{ backgroundColor: '#f7fafc', padding: '2px 4px', borderRadius: '3px', fontSize: '11px' }}>```language</code> then code then <code style={{ backgroundColor: '#f7fafc', padding: '2px 4px', borderRadius: '3px', fontSize: '11px' }}>```</code></li>
                <li>Supported: javascript, python, java, sql, csharp, typescript</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Problem Statement Preview */}
        {formData.problemStatement && formData.problemStatement.trim() && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
              📋 Preview
            </label>
            <div style={{
              padding: '16px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: '#fefefe',
              minHeight: '80px',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#2d3748'
            }}>
              <FormattedText text={formData.problemStatement} />
            </div>
          </div>
        )}

        {/* Additional Instructions for AI */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Additional Instructions for AI (Optional)
          </label>
          
          {/* Layout with textarea on left and templates on right */}
          <div style={{ display: 'grid', gridTemplateColumns: instructionTemplates.length > 0 ? '1fr 320px' : '1fr', gap: '16px' }}>
            {/* Textarea Section */}
            <div>
              <textarea
                value={formData.additionalInstructions}
                onChange={(e) => {
                  handleInputChange('additionalInstructions', e.target.value);
                  // Clear selected template if user manually edits
                  if (selectedTemplate && e.target.value !== instructionTemplates.find(t => t._id === selectedTemplate)?.content) {
                    setSelectedTemplate(null);
                  }
                }}
                placeholder="E.g., 'Focus on error handling', 'Include examples with arrays', 'Emphasize time complexity', etc."
                rows={8}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border 0.15s'
                }}
              />
              <small style={{ color: '#718096', fontSize: '12px', marginTop: '6px', display: 'block' }}>
                Provide specific guidance for the AI when generating algorithm steps and hints.
              </small>
            </div>
            
            {/* Templates Panel */}
            {instructionTemplates.length > 0 && (
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#4a5568', 
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>💡</span>
                  <span>Quick Templates</span>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {instructionTemplates.map(template => (
                    <div
                      key={template._id}
                      onClick={() => handleTemplateSelect(template._id)}
                      style={{
                        padding: '10px',
                        backgroundColor: selectedTemplate === template._id ? '#eef2ff' : 'white',
                        border: selectedTemplate === template._id ? '2px solid #667eea' : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontSize: '13px'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedTemplate !== template._id) {
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                          e.currentTarget.style.borderColor = '#cbd5e0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedTemplate !== template._id) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      <div style={{ 
                        fontWeight: '600', 
                        color: '#2d3748',
                        marginBottom: '4px',
                        fontSize: '12px'
                      }}>
                        {template.name}
                      </div>
                      {template.description && (
                        <div style={{ 
                          color: '#718096', 
                          fontSize: '11px',
                          marginBottom: '6px'
                        }}>
                          {template.description}
                        </div>
                      )}
                      <div style={{
                        color: '#4a5568',
                        fontSize: '11px',
                        backgroundColor: '#f1f5f9',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        lineHeight: '1.4',
                        maxHeight: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {template.content.length > 80 
                          ? template.content.substring(0, 80) + '...' 
                          : template.content}
                      </div>
                      {template.usageCount > 0 && (
                        <div style={{ 
                          fontSize: '10px', 
                          color: '#9ca3af',
                          marginTop: '4px'
                        }}>
                          📊 Used {template.usageCount} times
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div style={{ 
                  marginTop: '10px', 
                  paddingTop: '10px', 
                  borderTop: '1px solid #e2e8f0',
                  fontSize: '11px',
                  color: '#718096',
                  textAlign: 'center'
                }}>
                  Click a template to prefill
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Source Code Input (Alternative Approach) */}
        <div style={{ marginBottom: '0', borderTop: '1px dashed #e2e8f0', paddingTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            💻 Alternative: Provide Working Source Code (Optional)
          </label>
          <div style={{ 
            backgroundColor: '#f7fafc', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#4a5568', lineHeight: '1.5' }}>
              <strong>💡 Tip:</strong> If you have a working solution, paste it here and AI will reverse-engineer the algorithm, pseudocode, and problem statement from your code.
            </p>
          </div>
          
          {/* Language Selector for Source Code */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '12px', color: '#4a5568' }}>
              Source Code Language
            </label>
            <select
              value={formData.sourceCodeLanguage}
              onChange={(e) => handleInputChange('sourceCodeLanguage', e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                color: '#2d3748',
                cursor: 'pointer',
                width: '200px'
              }}
            >
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="sql">SQL</option>
              <option value="csharp">C#</option>
              <option value="typescript">TypeScript</option>
            </select>
          </div>
          
          {/* Source Code Textarea */}
          <textarea
            value={formData.sourceCode}
            onChange={(e) => handleInputChange('sourceCode', e.target.value)}
            placeholder={`// Paste your working ${formData.sourceCodeLanguage} solution here...\n// Example:\nfunction reverseString(str) {\n  return str.split('').reverse().join('');\n}`}
            rows={12}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              resize: 'vertical',
              transition: 'border 0.15s',
              lineHeight: '1.5'
            }}
          />
          <small style={{ color: '#718096', fontSize: '11px', marginTop: '6px', display: 'block' }}>
            ⚠️ Note: If source code is provided, the AI will generate algorithm steps and pseudocode based on this code instead of the problem statement.
          </small>
        </div>
      </div>

      {/* Configuration Section */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '24px', 
        borderRadius: '10px', 
        border: '1px solid #e2e8f0',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <h3 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>⚙️</span>
          Configuration
        </h3>

        {/* Complexity and Estimated Time in Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          {/* Complexity */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
              Complexity *
            </label>
            <Select
              value={{ value: formData.complexity, label: formData.complexity }}
              onChange={(option) => handleInputChange('complexity', option.value)}
              options={[
                { value: 'Easy', label: 'Easy' },
                { value: 'Medium', label: 'Medium' },
                { value: 'Hard', label: 'Hard' }
              ]}
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: '#e2e8f0',
                  '&:hover': {
                    borderColor: '#cbd5e0'
                  }
                }),
                menuPortal: (base) => ({ ...base, zIndex: 1050 })
              }}
            />
          </div>

          {/* Estimated Time */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
              Estimated Time (minutes)
            </label>
            <input
              type="number"
              value={formData.estimatedTime}
              onChange={(e) => handleInputChange('estimatedTime', parseInt(e.target.value))}
              min="5"
              step="5"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white'
              }}
            />
          </div>
        </div>

        {/* Developer Level */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Developer Level *
          </label>
          <small style={{ color: '#718096', fontSize: '12px', marginBottom: '12px', display: 'block' }}>
            Controls how AI breaks down the algorithm and generates hints
          </small>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {developerLevels.map((level) => (
              <div
                key={level.value}
                onClick={() => handleInputChange('developerLevel', level.value)}
                style={{
                  padding: '12px',
                  border: `2px solid ${formData.developerLevel === level.value ? level.color : '#e2e8f0'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: formData.developerLevel === level.value ? `${level.color}15` : '#fafafa',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                  <input
                    type="radio"
                    checked={formData.developerLevel === level.value}
                    onChange={() => {}}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                    readOnly
                  />
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: level.color,
                    marginRight: '8px',
                    flexShrink: 0
                  }} />
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                    {level.label}
                  </span>
                </div>
                <p style={{ margin: '0 0 0 32px', fontSize: '12px', color: '#718096', lineHeight: '1.4' }}>
                  {level.steps} • {level.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Programming Languages * (Select at least one)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
            {availableLanguages.map((lang) => (
              <div
                key={lang.value}
                onClick={() => handleLanguageToggle(lang.value)}
                style={{
                  padding: '10px 14px',
                  border: `2px solid ${formData.languages.includes(lang.value) ? '#667eea' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: formData.languages.includes(lang.value) ? '#eef2ff' : '#fafafa',
                  transition: 'all 0.15s',
                  textAlign: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={formData.languages.includes(lang.value)}
                    onChange={() => {}}
                    style={{ marginRight: '6px', cursor: 'pointer' }}
                    readOnly
                  />
                  <span style={{ 
                    fontWeight: formData.languages.includes(lang.value) ? '600' : '500',
                    fontSize: '14px',
                    color: formData.languages.includes(lang.value) ? '#667eea' : '#2d3748'
                  }}>
                    {lang.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {formData.languages.length > 0 && (
            <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#f0fff4', borderRadius: '6px', border: '1px solid #48bb78' }}>
              <span style={{ fontSize: '13px', color: '#2f855a', fontWeight: '600' }}>
                Selected: {formData.languages.join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Starter Code Mode */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Starter Code Mode *
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div
              onClick={() => handleInputChange('starterCodeMode', 'withFunction')}
              style={{
                padding: '12px',
                border: `2px solid ${formData.starterCodeMode === 'withFunction' ? '#667eea' : '#e2e8f0'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: formData.starterCodeMode === 'withFunction' ? '#eef2ff' : '#fafafa',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <input
                  type="radio"
                  checked={formData.starterCodeMode === 'withFunction'}
                  onChange={() => {}}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                  readOnly
                />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                  Complete Structure with TODO
                </span>
              </div>
              <p style={{ margin: '0 0 0 24px', fontSize: '12px', color: '#718096', lineHeight: '1.4' }}>
                Provide complete code with comments; only algorithm section as TODO
              </p>
            </div>
            <div
              onClick={() => handleInputChange('starterCodeMode', 'empty')}
              style={{
                padding: '12px',
                border: `2px solid ${formData.starterCodeMode === 'empty' ? '#667eea' : '#e2e8f0'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: formData.starterCodeMode === 'empty' ? '#eef2ff' : '#fafafa',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <input
                  type="radio"
                  checked={formData.starterCodeMode === 'empty'}
                  onChange={() => {}}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                  readOnly
                />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                  Empty Code Editor
                </span>
              </div>
              <p style={{ margin: '0 0 0 24px', fontSize: '12px', color: '#718096', lineHeight: '1.4' }}>
                Start with a blank editor for maximum challenge
              </p>
            </div>
          </div>
        </div>

        {/* Category */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Category
          </label>
          <Select
            value={formData.category ? categories.find(cat => cat._id === formData.category) ? { value: formData.category, label: categories.find(cat => cat._id === formData.category).name } : null : null}
            onChange={(option) => handleInputChange('category', option ? option.value : '')}
            options={categories.map(cat => ({ value: cat._id, label: cat.name }))}
            isClearable
            placeholder="Select a category..."
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                borderColor: '#e2e8f0',
                '&:hover': {
                  borderColor: '#cbd5e0'
                }
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1050 })
            }}
          />
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>
            Tags (Optional)
          </label>
          <CreatableSelect
            isMulti
            value={formData.tags}
            onChange={handleTagChange}
            placeholder="Type to create tags..."
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                borderColor: '#e2e8f0',
                '&:hover': {
                  borderColor: '#cbd5e0'
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#f0fff4',
                border: '1px solid #9ae6b4'
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: '#2f855a',
                fontWeight: '500'
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: '#2f855a',
                '&:hover': {
                  backgroundColor: '#c6f6d5',
                  color: '#22543d'
                }
              }),
              menuPortal: (base) => ({ ...base, zIndex: 1050 })
            }}
            formatCreateLabel={(inputValue) => `Create tag: "${inputValue}"`}
          />
          <small style={{ color: '#718096', fontSize: '12px', marginTop: '6px', display: 'block' }}>
            Type and press Enter to create new tags
          </small>
        </div>

      </div>

      {/* Action Button */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        padding: '20px 24px', 
        borderRadius: '10px', 
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        {cartMode && !isEditMode ? (
          <button
            onClick={handleAddToCart}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(72, 187, 120, 0.25)'
            }}
          >
            <span style={{ marginRight: '8px' }}>🛒</span>
            Add to Cart
          </button>
        ) : (
          <button
            onClick={handleGenerateSteps}
            disabled={generating}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: generating ? '#cbd5e0' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: generating ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: generating ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.25)'
            }}
          >
            {generating ? (
              <>
                <span style={{ marginRight: '8px' }}>✨</span>
                {isEditMode 
                  ? `Updating assignment${formData.languages && formData.languages.length > 0 ? ` (may regenerate algorithms for ${formData.languages.length} language${formData.languages.length > 1 ? 's' : ''})` : ''}...` 
                  : `Generating algorithms for ${formData.languages.length} language${formData.languages.length > 1 ? 's' : ''}... (this may take 30-60 seconds)`
                }
              </>
            ) : (
              <>
                <span style={{ marginRight: '8px' }}>{isEditMode ? '💾' : '🚀'}</span>
                {isEditMode ? 'Update Assignment' : 'Generate & Create Assignment'}
              </>
            )}
          </button>
        )}
      </div>

      {/* Preview Generated Steps */}
      {algorithmByLanguage && (
        <div style={{ 
          backgroundColor: '#ffffff', 
          padding: '24px', 
          borderRadius: '10px', 
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '18px', fontWeight: '600', margin: 0 }}>
              ✨ Generated Assignment Preview
            </h3>
            <span style={{ 
              padding: '6px 12px',
              backgroundColor: selectedLevelConfig?.color || '#667eea',
              color: 'white',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              {selectedLevelConfig?.label}
            </span>
          </div>

          {/* Language Tabs */}
          {formData.languages && formData.languages.length > 0 && (
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {formData.languages.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setPreviewLanguage(lang)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderBottom: previewLanguage === lang ? '3px solid #667eea' : '3px solid transparent',
                      background: previewLanguage === lang ? '#eef2ff' : 'transparent',
                      color: previewLanguage === lang ? '#667eea' : '#718096',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: previewLanguage === lang ? '600' : '500',
                      textTransform: 'capitalize',
                      transition: 'all 0.2s',
                      borderRadius: '6px 6px 0 0',
                      marginBottom: '-2px'
                    }}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Steps for selected preview language */}
          {renderedSteps}
        </div>
      )}

      {/* Pseudocode Preview and Edit */}
      {pseudocodeByLanguage && isEditMode && (
        <div style={{ 
          backgroundColor: '#ffffff', 
          padding: '24px', 
          borderRadius: '10px', 
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          marginTop: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '18px', fontWeight: '600', margin: 0, flex: 1 }}>
              📝 Pseudocode by Language
            </h3>
            <span style={{ fontSize: '13px', color: '#718096', fontStyle: 'italic' }}>
              Click "Edit" to modify pseudocode
            </span>
          </div>

          {/* Language Tabs for Pseudocode */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {Object.keys(pseudocodeByLanguage).map((lang) => (
              <div key={lang} style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '10px',
                padding: '16px',
                flex: '1',
                minWidth: '300px',
                backgroundColor: '#f7fafc'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <h4 style={{ 
                    color: '#667eea', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    margin: 0
                  }}>
                    {lang}
                  </h4>
                  {editingPseudocode?.language === lang ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setSavingPseudocode(true);
                            setError(null);
                            const response = await assignmentAPI.updatePseudocode(
                              assignment._id,
                              lang,
                              editingPseudocode.text
                            );
                            setPseudocodeByLanguage(response.data.pseudocodeByLanguage);
                            setEditingPseudocode(null);
                            setSuccess('Pseudocode updated successfully!');
                            setTimeout(() => setSuccess(null), 3000);
                          } catch (err) {
                            setError(err.response?.data?.message || 'Failed to update pseudocode');
                          } finally {
                            setSavingPseudocode(false);
                          }
                        }}
                        disabled={savingPseudocode}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: '#48bb78',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: savingPseudocode ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          opacity: savingPseudocode ? 0.6 : 1
                        }}
                      >
                        {savingPseudocode ? '💾 Saving...' : '💾 Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPseudocode(null)}
                        disabled={savingPseudocode}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: '#e2e8f0',
                          color: '#4a5568',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: savingPseudocode ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}
                      >
                        ❌ Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingPseudocode({ language: lang, text: pseudocodeByLanguage[lang] })}
                      style={{
                        padding: '6px 14px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </div>

                {editingPseudocode?.language === lang ? (
                  <textarea
                    value={editingPseudocode.text}
                    onChange={(e) => setEditingPseudocode({ ...editingPseudocode, text: e.target.value })}
                    style={{
                      width: '100%',
                      minHeight: '300px',
                      padding: '12px',
                      border: '2px solid #667eea',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: 'Monaco, Courier, monospace',
                      lineHeight: '1.6',
                      resize: 'vertical'
                    }}
                    placeholder="Enter pseudocode..."
                  />
                ) : (
                  <pre style={{
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    overflow: 'auto',
                    margin: 0,
                    fontFamily: 'Monaco, Courier, monospace',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}>
                    {pseudocodeByLanguage[lang] || 'No pseudocode available'}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default CodingAssignmentForm;
